import { makeAutoObservable, runInAction } from 'mobx';
import { Match } from 'navigo';

import { DiligenceOutput } from 'dtos/diligence/DiligenceOutput';
import { missingDropboxLinkMessage, noAccessToJobMessage } from 'dtos/users/common';

import { AuthStore } from 'stores/users/AuthStore';
import { SavedEvent } from 'stores/diligence/DiligenceAdminStore';
import { DiligenceStore } from 'stores/diligence/DiligenceStore';
import { JobsListStore } from 'stores/jobs/JobsListStore';

import { googleMapsApiKey, serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { IRouteStore } from 'stores/RouteStore';
import { HttpException } from 'utils/types';

export class DiligencePageStore {
  token: string = '';
  diligenceStore?: DiligenceStore;

  propertyCoordinates?: { latitude: number; longitude: number };
  propertyCoordinatesAreLoading = true;
  loadStatus: 'loading' | 'error' | 'dropboxError' | 'loaded' = 'loading';

  constructor(
    private readonly authStore: AuthStore,
    private readonly jobsListStore: JobsListStore,
    private readonly routeStore: IRouteStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    const token = match?.data?.token;
    if (token) {
      this.loadJob(token);
    }

    this.jobsListStore.ensureJobsLoaded();
  }

  async loadJob(token: string) {
    this.loadStatus = 'loading';
    this.token = token;
    this.diligenceStore = undefined;
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/diligence/${encodeURIComponent(this.token)}`);
      if (!response.ok) {
        const exception: HttpException = nullsToUndefined(await response.json());
        if (response.status === 403 && exception.message === noAccessToJobMessage) {
          this.routeStore.navigateByName('requestAccess', { token: this.token });
        }
        runInAction(() => {
          this.loadStatus = exception.message === missingDropboxLinkMessage ? 'dropboxError' : 'error';
        });
        return;
      }
      const job: DiligenceOutput = nullsToUndefined(await response.json());
      runInAction(async () => {
        this.loadStatus = 'loaded';
        this.diligenceStore = new DiligenceStore(token, job, this.authStore);
        this.diligenceStore.events.addEventListener(SavedEvent.eventType, this.reloadJob);
        await this.diligenceStore.activate();
      });

      await this.loadPropertyCoordinates(job);
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.loadStatus = 'error';
        this.diligenceStore = undefined;
        this.propertyCoordinates = undefined;
        this.propertyCoordinatesAreLoading = false;
      });
    }
  }

  private async loadPropertyCoordinates(job: DiligenceOutput) {
    this.propertyCoordinates = undefined;
    this.propertyCoordinatesAreLoading = true;

    let coordinates: { latitude: number; longitude: number } | undefined;
    if (job.coordinates?.latitude && job.coordinates?.longitude) {
      coordinates = {
        latitude: job.coordinates.latitude,
        longitude: job.coordinates.longitude,
      };
    } else {
      coordinates = await this.getPropertyCoordinates(job);
    }

    const streetViewMetadata = await this.getStreetViewMetadata(coordinates);

    runInAction(() => {
      if (streetViewMetadata?.status === 'OK') {
        this.propertyCoordinates = coordinates;
      }

      this.propertyCoordinatesAreLoading = false;
    });
  }

  private async getPropertyCoordinates(job: DiligenceOutput) {
    try {
      const address = job.address;

      if (!address) {
        return;
      }

      const encodedAddress = encodeURIComponent(`${address.street} ${address.city} ${address.state} ${address.zip}`);

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${googleMapsApiKey}`
      );
      const data = await response.json();

      const coordinates = data.results[0].geometry.location;
      if (coordinates?.lat && coordinates?.lng) {
        return {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        };
      }
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  private async getStreetViewMetadata(coordinates?: { latitude: number; longitude: number }) {
    try {
      if (!coordinates) return;

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/streetview/metadata?location=${coordinates.latitude},${coordinates.longitude}&key=${googleMapsApiKey}`
      );

      const data = await response.json();
      return data;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  reloadJob = () => {
    if (this.diligenceStore) {
      this.diligenceStore.destroy();
      this.diligenceStore = undefined;
    }
    this.loadJob(this.token);
  };
}
